import type { PortableTextComponents } from '@portabletext/react';
import type { PortableTextBlock } from '@portabletext/types';

/**
 * Render map for FAQ answers. Reproduces the markup the FAQ used before it moved
 * to the CMS: paragraphs, a bullet list, bold, and underlined links (relative or
 * absolute, optionally opening in a new tab).
 */
export const faqAnswerComponents: PortableTextComponents = {
    block: {
        normal: ({ children }) => <p>{children}</p>,
    },
    list: {
        bullet: ({ children }) => (
            <ul className="list-disc list-inside flex flex-col gap-3 -mt-5">{children}</ul>
        ),
    },
    listItem: {
        bullet: ({ children }) => <li>{children}</li>,
    },
    marks: {
        strong: ({ children }) => <strong>{children}</strong>,
        link: ({ children, value }) => {
            const href = (value?.href as string) ?? '#';
            const blank = Boolean(value?.blank);
            return (
                <a
                    href={href}
                    className="underline mouse-hover:no-underline"
                    {...(blank ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                >
                    {children}
                </a>
            );
        },
    },
};

/** Flatten Portable Text to plain text (for FAQPage JSON-LD / SEO). */
export function portableTextToPlain(blocks: PortableTextBlock[] | undefined): string {
    if (!blocks) return '';
    return blocks
        .map((block) => {
            const children = (block as { children?: { text?: string }[] }).children;
            if (block._type !== 'block' || !Array.isArray(children)) return '';
            return children.map((child) => child.text ?? '').join('');
        })
        .filter(Boolean)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
}

// --- Legacy (messages JSON) → Portable Text -------------------------------
// Used as a runtime fallback when the Sanity FAQ documents aren't populated.
// The one-time migration script mirrors this logic to seed the CMS.

let keyCounter = 0;
const nextKey = (prefix: string) => `${prefix}${keyCounter++}`;

function decodeEntities(input: string): string {
    return input
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ');
}

type Span = { _type: 'span'; _key: string; text: string; marks: string[] };
type MarkDef = { _type: 'link'; _key: string; href: string; blank: boolean };

/** Parse a snippet of the legacy inline HTML (<b>/<strong>, <a href target>) into spans + markDefs. */
function parseInline(html: string): { children: Span[]; markDefs: MarkDef[] } {
    const children: Span[] = [];
    const markDefs: MarkDef[] = [];
    const tagRe = /<(\/?)(b|strong|a)\b([^>]*)>/gi;
    const activeDecorators: string[] = [];
    let activeLinkKey: string | null = null;
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    const pushText = (raw: string) => {
        const text = decodeEntities(raw);
        if (!text) return;
        const marks = [...activeDecorators];
        if (activeLinkKey) marks.push(activeLinkKey);
        children.push({ _type: 'span', _key: nextKey('s'), text, marks });
    };

    while ((match = tagRe.exec(html)) !== null) {
        pushText(html.slice(lastIndex, match.index));
        lastIndex = tagRe.lastIndex;
        const closing = match[1] === '/';
        const tag = match[2].toLowerCase();
        const attrs = match[3] || '';

        if (tag === 'a') {
            if (closing) {
                activeLinkKey = null;
            } else {
                const href = attrs.match(/href\s*=\s*['"]([^'"]*)['"]/i);
                const target = attrs.match(/target\s*=\s*['"]([^'"]*)['"]/i);
                const key = nextKey('link');
                markDefs.push({
                    _type: 'link',
                    _key: key,
                    href: href ? decodeEntities(href[1]) : '#',
                    blank: target ? target[1] === '_blank' : false,
                });
                activeLinkKey = key;
            }
        } else if (closing) {
            const i = activeDecorators.lastIndexOf('strong');
            if (i !== -1) activeDecorators.splice(i, 1);
        } else {
            activeDecorators.push('strong');
        }
    }
    pushText(html.slice(lastIndex));

    if (children.length === 0) {
        children.push({ _type: 'span', _key: nextKey('s'), text: '', marks: [] });
    }
    return { children, markDefs };
}

/**
 * Convert a legacy FAQ answer (`(string | string[])[]` — paragraphs and bullet
 * lists, with inline HTML) into Portable Text blocks.
 */
export function legacyAnswerToPortableText(answer: (string | string[])[]): PortableTextBlock[] {
    const blocks: PortableTextBlock[] = [];

    for (const item of answer) {
        if (Array.isArray(item)) {
            for (const li of item) {
                const { children, markDefs } = parseInline(li);
                blocks.push({
                    _type: 'block',
                    _key: nextKey('b'),
                    style: 'normal',
                    listItem: 'bullet',
                    level: 1,
                    markDefs,
                    children,
                } as unknown as PortableTextBlock);
            }
        } else {
            const { children, markDefs } = parseInline(item);
            blocks.push({
                _type: 'block',
                _key: nextKey('b'),
                style: 'normal',
                markDefs,
                children,
            } as unknown as PortableTextBlock);
        }
    }

    return blocks;
}
